import React, { useContext, lazy, Suspense } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import "./styles.css";
import {
  GeneralProvider,
  GeneralContext,
} from "@services/providers/GeneralProvider";
import {
  createBrowserRouter,
  RouterProvider,
  // Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { setRedirectToLogin } from "@services/api/axiosInstance";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";
import { DDRProvider } from "./services/providers/DDRProvider";

// import HomePage from "@pages/homepage/Index";
const HomePage = lazy(() => import("@pages/homepage/Index"));
const StoryBook = lazy(() => import("@pages/StoryBook"));
import ErrorPage from "@pages/ErrorPage";
const Home = lazy(() => import("@pages/homepage/home/Home"));
const Threatscore = lazy(() => import("@pages/homepage/ThreatscoreQuadrant"));
const Identity = lazy(
  () => import("@pages/homepage/identityIntelligence/IdentityIntelligence"),
);
const DDRIdentity = lazy(
  () =>
    import("@pages/homepage/ddr-identityIntelligence/DDRIdentityIntelligence"),
);
const AssetAutoDiscovery = lazy(
  () => import("@pages/homepage/asset-auto-discovery/AssetAutoDiscovery"),
);
const BlockCountRatio = lazy(
  () => import("@pages/homepage/blockcountRatio/BlockcountRatio"),
);
const AMLAnomalyDetection = lazy(
  () => import("@pages/homepage/aml-anomaly-detection/AmlAnomalyDetection"),
);
const RiskCompliance = lazy(
  () => import("@pages/homepage/risk-compliance/RiskCompliance"),
);
const ManagementDashboard = lazy(
  () => import("@pages/homepage/management-dashboard/ManagementDashboard"),
);
const S360 = lazy(() => import("@pages/s360/S360"));
const UsecasesBuilder = lazy(
  () => import("@pages/homepage/usecase-builder/UsecaseBuilder"),
);
const UsecaseListTemp = lazy(
  () => import("@pages/homepage/usecase-builder/UsecaseListTemp"),
);
const AIEngine = lazy(() => import("@pages/homepage/ai-engine/AIEngine"));
const CaseManagement = lazy(
  () => import("@pages/homepage/case-management/CaseManagement"),
);
const CreateDashboard = lazy(
  () => import("@pages/homepage/ai-engine/ai-engine-visual/CreateDashboard"),
);
const LiveShadow = lazy(() => import("@pages/homepage/liveshadow/LiveShadow"));
const SecurityPosture = lazy(
  () => import("@pages/homepage/security-posture/SecurityPosture"),
);
const Services = lazy(() => import("@pages/homepage/service/Services"));
const SensitiveData = lazy(
  () => import("@pages/homepage/sensitive-data/SensitiveData"),
);
const DataRepository = lazy(
  () => import("@pages/homepage/data-repo/DataRepository"),
);
const DataSovereignty = lazy(
  () => import("@pages/homepage/data-sovereignty/DataSovereignty"),
);
const ComplianceDashboard = lazy(
  () => import("@pages/homepage/compliance-dashboard/ComplianceDashboard"),
);

const DataExposure = lazy(
  () => import("@pages/homepage/data-exposure/DataExposure"),
);

const DDRAssetAutoDiscovery = lazy(
  () => import("@pages/homepage/ddr-asset-auto-discovery/AssetAutoDiscovery"),
);

const CustomDashboard = lazy(
  () => import("@pages/homepage/customDashboard/CustomDashboard"),
);

const IdentityPosture = lazy(
  () => import("@pages/homepage/identity-posture/IdentityPosture"),
);

const UserDirectory = lazy(
  () => import("@pages/homepage/identityIntelligence/UserDirectory"),
);

const ContinuousMonitoring = lazy(
  () => import("@pages/homepage/continuousMonitoring/ContinuousMonitoring"),
);

const RiskRemediation = lazy(
  () => import("@pages/homepage/riskRemediation/RiskRemediation"),
);

const PluginCompliance = lazy(
  () => import("@pages/homepage/plugin-compliance/PluginCompliance"),
);

const BuilderReactFlow = lazy(
  () => import("@pages/homepage/widgetBuilder/BuilderReactFlow"),
);

const AutomationBuilder = lazy(
  () => import("@pages/homepage/automationBuilder/AutomationBuilder"),
);

const UploadDocument = lazy(
  () => import("@pages/homepage/ai-engine/ai-engine-processing/UploadDocument"),
);

const DashboardBuilder = lazy(
  () => import("@pages/homepage/customDashboard/DashboardBuilder"),
);

import { Toaster } from "@components/ui/toaster";
import License from "@pages/License";
import CreatePlugin from "@pages/plugin/CreatePlugin";
// import License from "./pages/License";
import {
  initializeErrorHandling,
  removeErrorHandling,
} from "./utils/jsErrorLogger";
import SuspensePageLoader from "./components/loaders/SuspensePageLoader";
import CreatePluginNode from "./pages/CreatePlugin/FlowBuilder";
import CreatePluginUIPage from "./pages/CreatePlugin/UIPage";
import CreateMeshPlugin from "./pages/CreatePlugin/CreateMeshPlugin";

const ndrRoutes = [
  {
    path: "threatscore-quadrant",
    element: <Threatscore />,
  },
  {
    path: "identity-intelligence",
    element: <Identity />,
  },
  {
    path: "asset-autodiscovery",
    element: <AssetAutoDiscovery />,
  },
  {
    path: "blockcount-ratio",
    element: <BlockCountRatio />,
  },
  {
    path: "usecase-builder",
    element: <UsecasesBuilder />,
  },
  {
    path: "usecase-list",
    element: <UsecaseListTemp />,
  },
  {
    path: "/shadow360",
    element: <S360 />,
  },
  {
    path: "management-dashboard",
    element: <ManagementDashboard />,
  },
  {
    path: "/case-management",
    element: <CaseManagement />,
  },
  {
    path: "custom-dashboard/:dashboard",
    children: [
      {
        path: "",
        element: <CustomDashboard />,
      },
      {
        path: "getEdit/",
        element: <CreateDashboard />,
      },
    ],
  },
  {
    path: "aml-anomaly-detection",
    element: <AMLAnomalyDetection />,
  },
  {
    path: "ai-engine/*",
    element: <AIEngine />,
    // children: [
    //   {
    //     path: "",
    //     element: <AIEngine />,
    //   },
    //   {
    //     path: "process",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineProcessing />,
    //       },
    //       {
    //         path: "dataset",
    //         element: <DataSet />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "visual",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineVisual />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "response",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineResponse />,
    //       },
    //     ],
    //   },
    // ],
  },
  {
    path: "management-dashboard",
    element: <ManagementDashboard />,
  },
  {
    path: "/case-management",
    element: <CaseManagement />,
  },
  {
    path: "/widget-builder",
    element: (
      <ReactFlowProvider>
        <BuilderReactFlow />
      </ReactFlowProvider>
    ),
  },
  {
    path: "/automation-builder",
    element: <AutomationBuilder />,
  },
  {
    path: "dashboard-builder",
    element: <DashboardBuilder />,
  },
];

const cmxRoutes = [
  {
    path: "usecase-builder",
    element: <UsecasesBuilder />,
  },
  {
    path: "/shadow360",
    element: <S360 />,
  },
  {
    path: "custom-dashboard/:dashboard",
    children: [
      {
        path: "",
        element: <CustomDashboard />,
      },
      {
        path: "getEdit/",
        element: <CreateDashboard />,
      },
    ],
  },
  {
    path: "identity-intelligence",
    element: <Identity />,
  },
  {
    path: "asset-autodiscovery",
    element: <AssetAutoDiscovery />,
  },
  {
    path: "aml-anomaly-detection",
    element: <AMLAnomalyDetection />,
  },
  {
    path: "data-inventory",
    element: <DataRepository />,
  },
  {
    path: "ai-engine/*",
    element: <AIEngine />,
    // children: [
    //   {
    //     path: "",
    //     element: <AIEngine />,
    //   },
    //   {
    //     path: "process",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineProcessing />,
    //       },
    //       {
    //         path: "dataset",
    //         element: <DataSet />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "visual",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineVisual />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "response",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineResponse />,
    //       },
    //     ],
    //   },
    // ],
  },
  {
    path: "/case-management",
    element: <CaseManagement />,
  },
  {
    path: "/automation-builder",
    element: <AutomationBuilder />,
  },
  // {
  //   path: "create-dashboard",
  //   element: <CreateDashboard />,
  // },
];

const itdrRoutes = [
  {
    path: "security-exposures",
    element: <PluginCompliance />,
  },
  {
    path: "user-directory",
    element: <UserDirectory />,
  },
  {
    path: "identity-intelligence",
    element: <Identity />,
  },
  {
    path: "identity-posture",
    element: <IdentityPosture />,
  },
  {
    path: "continuous-monitoring",
    element: <ContinuousMonitoring />,
  },
  {
    path: "aml-anomaly-detection",
    element: <RiskCompliance />,
  },
  {
    path: "risk-remediation",
    element: <RiskRemediation />,
  },
  {
    path: "/shadow360",
    element: <S360 />,
  },
  {
    path: "behavior-analytics",
    element: <DataExposure />,
  },
  {
    path: "/compliance-dashboard",
    element: <ComplianceDashboard />,
  },
  {
    path: "ai-engine/*",
    element: <AIEngine />,
    // children: [
    //   {
    //     path: "",
    //     element: <AIEngine />,
    //   },
    //   {
    //     path: "process",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineProcessing />,
    //       },
    //       {
    //         path: "dataset",
    //         element: <DataSet />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "visual",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineVisual />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "response",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineResponse />,
    //       },
    //     ],
    //   },
    // ],
  },
  {
    path: "usecase-builder",
    element: <UsecasesBuilder />,
  },
  {
    path: "/case-management",
    element: <CaseManagement />,
  },
  {
    path: "custom-dashboard/:dashboard",
    children: [
      {
        path: "",
        element: <CustomDashboard />,
      },
      {
        path: "getEdit/",
        element: <CreateDashboard />,
      },
    ],
  },
  // {
  //   path: "create-dashboard",
  //   element: <CreateDashboard />,
  // },
];

const ddrRoutes = [
  {
    path: "liveshadow",
    element: <LiveShadow />,
  },
  {
    path: "data-exposure",
    element: <DataExposure />,
  },
  {
    path: "data-inventory",
    element: <DataRepository />,
  },
  {
    path: "data-sovereignty",
    element: <DataSovereignty />,
  },
  {
    path: "identity-intelligence",
    element: <DDRIdentity />,
  },
  {
    path: "risk-compliance",
    element: <RiskCompliance />,
  },
  {
    path: "sensitive-data",
    element: <SensitiveData />,
  },
  {
    path: "/shadow360",
    element: <S360 />,
  },
  {
    path: "/security-posture",
    element: <SecurityPosture />,
  },
  {
    path: "/asset-autodiscovery",
    element: <DDRAssetAutoDiscovery />,
  },
  {
    path: "/compliance-dashboard",
    element: <ComplianceDashboard />,
  },
  {
    path: "services",
    element: <Services />,
  },
  {
    path: "usecase-builder",
    element: <UsecasesBuilder />,
  },
  {
    path: "custom-dashboard/:dashboard",
    children: [
      {
        path: "",
        element: <CustomDashboard />,
      },
      {
        path: "getEdit/",
        element: <CreateDashboard />,
      },
    ],
  },
  {
    path: "ai-engine/*",
    element: <AIEngine />,
    // children: [
    //   {
    //     path: "",
    //     element: <AIEngine />,
    //   },
    //   {
    //     path: "process",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineProcessing />,
    //       },
    //       {
    //         path: "dataset",
    //         element: <DataSet />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "visual",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineVisual />,
    //       },
    //     ],
    //   },
    //   {
    //     path: "response",
    //     children: [
    //       {
    //         path: "",
    //         element: <AiEngineResponse />,
    //       },
    //     ],
    //   },
    // ],
  },
  {
    path: "/case-management",
    element: <CaseManagement />,
  },
  {
    path: "/ai-engine/process/documentclassification/",
    element: <UploadDocument />,
  },
  // {
  //   path: "create-dashboard",
  //   element: <CreateDashboard />,
  // },
];

const generateRoutesObject = () => {
  const selectedRoutes: any = [];
  const { productType } = useContext(GeneralContext);
  const productSplit = productType?.split(",") || [];

  if (productSplit.includes("ndr")) {
    selectedRoutes.push(...ndrRoutes);
  }
  if (productSplit.includes("itdr")) {
    selectedRoutes.push(...itdrRoutes);
  }
  if (productSplit.includes("ddr")) {
    selectedRoutes.push(...ddrRoutes);
  }
  if (productSplit.includes("cmx")) {
    selectedRoutes.push(...cmxRoutes);
  }

  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: <HomePage />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "",
            element: isFeatureAllowed(ALL_KEYS.MAIN_HOME_PAGE_1) ? (
              <Home />
            ) : isFeatureAllowed(ALL_KEYS.MAIN_HOME_PAGE_2) ? (
              <SecurityPosture />
            ) : isFeatureAllowed(ALL_KEYS.MAIN_HOME_PAGE_3) ? (
              <IdentityPosture />
            ) : isFeatureAllowed(ALL_KEYS.MAIN_HOME_PAGE_4) ? (
              <LiveShadow />
            ) : (
              <></>
            ),
          },
          {
            path: "/create-plugin",
            element: <CreatePlugin />,
          },
          {
            path: "/create-node-plugin/info",
            element: <CreateMeshPlugin />,
          },
          {
            path: "/create-node-plugin/ui",
            element: <CreatePluginUIPage />,
          },
          {
            path: "/create-node-plugin/flow",
            element: (
              <ReactFlowProvider>
                <CreatePluginNode />
              </ReactFlowProvider>
            ),
          },
          ...selectedRoutes,
        ],
      },
      {
        path: "/storybook",
        element: <StoryBook />,
      },
      {
        path: "/license",
        element: <License />,
      },
    ],
    {
      basename: process.env.PUBLIC_URL,
    },
  );
  return router;
};

const App: React.FC = () => {
  return (
    <GeneralProvider>
      <AppContent />
    </GeneralProvider>
  );
};

const AppContent: React.FC = () => {
  const { theme, productType } = useContext(GeneralContext);
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  React.useEffect(() => {
    setRedirectToLogin(() => {
      let loginUrl = "/login";
      if (location.pathname)
        loginUrl += "/?next=/" + location.pathname.replace("/", "");
      window.location.href = loginUrl;
    });
  }, []);

  React.useEffect(() => {
    initializeErrorHandling();
    return () => {
      removeErrorHandling();
    };
  }, []);

  if (
    productType &&
    (productType?.split(",").includes("ddr") ||
      productType?.split(",").includes("cmx"))
  ) {
    return (
      <main className={theme}>
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <DDRProvider>
            <Suspense fallback={<SuspensePageLoader />}>
              <RouterProvider router={generateRoutesObject()} />
            </Suspense>
          </DDRProvider>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        </QueryClientProvider>
      </main>
    );
  }

  return (
    <main className={theme}>
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<SuspensePageLoader />}>
          <RouterProvider router={generateRoutesObject()} />
        </Suspense>
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </QueryClientProvider>
    </main>
  );
};

export default App;
